import { useState, useEffect, useCallback } from 'react';
import { Modal, Badge, Button, Skeleton, SimpleSwitch } from '@/components/ui';
import { PoolService } from '@/services/pool.service';
import { useToast } from '@/contexts/ToastContext';
import type { Pool, PoolUsage } from '@/types/pool.types';
import { Search, Plus, Trash2, AlertTriangle } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function PoolManagementModal({ isOpen, onClose }: Props) {
  const { showToast } = useToast();
  const [pools, setPools] = useState<Pool[]>([]);
  const [usage, setUsage] = useState<Record<number, PoolUsage>>({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [newPoolName, setNewPoolName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const allPools = await PoolService.getAllPools();
      setPools(allPools);

      // Load usage for all pools in parallel
      const usageResults = await Promise.allSettled(
        allPools.map((p) => PoolService.getPoolUsage(p.poolId))
      );
      const usageMap: Record<number, PoolUsage> = {};
      usageResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          usageMap[result.value.poolId] = result.value;
        }
      });
      setUsage(usageMap);
    } catch {
      showToast('Failed to load pools', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setSearch('');
      setShowAddForm(false);
      setNewPoolName('');
    }
  }, [isOpen, loadData]);

  const filtered = pools.filter(
    (p) => !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async () => {
    if (!newPoolName.trim()) return;
    try {
      setCreating(true);
      const created = await PoolService.createPool({ name: newPoolName.trim() });
      setPools((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      showToast(`Pool "${created.name}" created`, { variant: 'success' });
      setNewPoolName('');
      setShowAddForm(false);
      // Reload usage for the new pool
      try {
        const newUsage = await PoolService.getPoolUsage(created.poolId);
        setUsage((prev) => ({ ...prev, [created.poolId]: newUsage }));
      } catch {
        /* usage will show as unknown */
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to create pool', {
        variant: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (pool: Pool) => {
    try {
      setTogglingId(pool.poolId);
      const updated = await PoolService.togglePool(pool.poolId);
      setPools((prev) =>
        prev.map((p) => (p.poolId === pool.poolId ? { ...p, isActive: updated.isActive } : p))
      );
      showToast(`${pool.name} ${updated.isActive ? 'activated' : 'deactivated'}`, {
        variant: 'success',
      });
    } catch {
      showToast('Failed to toggle pool status', { variant: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (pool: Pool) => {
    try {
      setDeletingId(pool.poolId);
      await PoolService.deletePool(pool.poolId);
      setPools((prev) => prev.filter((p) => p.poolId !== pool.poolId));
      showToast(`Pool "${pool.name}" deleted`, { variant: 'success' });
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to delete pool', {
        variant: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const getUsageText = (poolId: number) => {
    const u = usage[poolId];
    if (!u) return 'Loading...';
    if (u.totalUsage === 0) return 'No usage';
    const parts: string[] = [];
    if (u.companies > 0) parts.push(`${u.companies} companies`);
    if (u.pageTypeNames > 0) parts.push(`${u.pageTypeNames} page types`);
    if (u.reports > 0) parts.push(`${u.reports} reports`);
    return parts.join(', ');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Pools" size="xl" showCloseButton>
      {/* Search + Add */}
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search pools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-600 dark:bg-dark-700 dark:text-white dark:placeholder-gray-500"
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowAddForm(!showAddForm)}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Add
        </Button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="mb-4 flex gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-dark-600 dark:bg-dark-700">
          <input
            type="text"
            placeholder="New pool name..."
            value={newPoolName}
            onChange={(e) => setNewPoolName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-500 dark:bg-dark-600 dark:text-white"
            autoFocus
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreate}
            disabled={creating || !newPoolName.trim()}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
            Cancel
          </Button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="space-y-3 pb-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          {search ? 'No pools match your search' : 'No pools available'}
        </div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto pb-4">
          {/* Desktop table */}
          <table className="hidden w-full md:table">
            <thead className="sticky top-0 z-10 bg-white dark:bg-dark-800">
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-dark-600 dark:text-gray-400">
                <th className="pb-3 pr-4">Pool Name</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3 pr-4">Usage</th>
                <th className="w-24 pb-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filtered.map((pool) => (
                <tr key={pool.poolId}>
                  <td className="py-3 pr-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {pool.name}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <SimpleSwitch
                        checked={pool.isActive}
                        onChange={() => handleToggle(pool)}
                        disabled={togglingId === pool.poolId}
                      />
                      <Badge variant={pool.isActive ? 'success' : 'error'} size="sm">
                        {pool.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {getUsageText(pool.poolId)}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    {usage[pool.poolId]?.canDelete ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(pool)}
                        disabled={deletingId === pool.poolId}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    ) : (
                      <span
                        className="inline-flex cursor-not-allowed items-center p-1.5 text-gray-300 dark:text-gray-600"
                        title="Cannot delete — pool is in use"
                      >
                        <Trash2 className="h-4 w-4" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile card list */}
          <div className="space-y-3 md:hidden">
            {filtered.map((pool) => (
              <div
                key={pool.poolId}
                className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-600 dark:bg-dark-800"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {pool.name}
                    </span>
                    <Badge variant={pool.isActive ? 'success' : 'error'} size="sm">
                      {pool.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {usage[pool.poolId]?.canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(pool)}
                      disabled={deletingId === pool.poolId}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    {usage[pool.poolId]?.totalUsage ? (
                      <>
                        <AlertTriangle className="h-3 w-3" />
                        {getUsageText(pool.poolId)}
                      </>
                    ) : (
                      getUsageText(pool.poolId)
                    )}
                  </div>
                  <SimpleSwitch
                    checked={pool.isActive}
                    onChange={() => handleToggle(pool)}
                    disabled={togglingId === pool.poolId}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
